import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Training,
  Theme,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
} from '@teo/components/icons';
import { IconTile } from '@teo/components';
import { ActiveCardPreview } from './ActiveCardPreview';
import { categoriesIcon } from '@/components/LearningPath/util';

const xStep = 190;

function hashInt(...nums) {
  let h = 2166136261 >>> 0;
  for (const n of nums) {
    const x = (n | 0) >>> 0;
    h ^= x + 0x9e3779b9 + ((h << 6) >>> 0) + (h >>> 2);
    h >>>= 0;
  }
  return h >>> 0;
}
function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rngFor = (childId, parentId) =>
  mulberry32(hashInt(childId, parentId ?? 0));

const DEFAULT_RANDOMNESS = {
  distanceFactorRange: [0.85, 1.35],
};

const SIBLING_GAP = 100;

function buildZigZagTree(
  arr,
  parentId = null,
  startX = 0,
  startY = 0,
  direction = 1,
  visited = new Map(),
  edges = [],
  randomness = DEFAULT_RANDOMNESS,
  angleDeg = 100
) {
  const nodes = [];

  const children = arr.filter((el) =>
    parentId === null ? el.roots.length === 0 : el.roots.includes(parentId)
  );

  children.sort((a, b) =>
    b.mandatory === a.mandatory ? a.id - b.id : b.mandatory ? 1 : -1
  );

  const angleRad = (angleDeg * Math.PI) / 180;

  children.forEach((child, idx) => {
    if (parentId !== null) {
      edges.push({
        from: parentId,
        to: child.id,
        mandatory: child.mandatory,
        progress: child?.progress,
      });
    }

    if (visited.has(child.id)) return;

    const r = rngFor(child.id, parentId);

    const distFactor =
      randomness.distanceFactorRange[0] +
      (randomness.distanceFactorRange[1] - randomness.distanceFactorRange[0]) *
        r();

    const distance = xStep * distFactor;
    const dx = distance * Math.cos(angleRad);
    const dyBase = distance * Math.sin(angleRad) * direction;

    const siblingOffset = (idx - (children.length - 1) / 2) * SIBLING_GAP;

    const x = startX + dx;

    // ВАЖЛИВО: siblings завжди рахуються від startY,
    // а не від offsetY, щоб вони не провалювалися вниз.
    const y = startY + dyBase + siblingOffset;

    visited.set(child.id, { x, y });

    nodes.push({
      id: child.id,
      name: child.name || '',
      position: { x, y },
      type: child.type,
      subType: child.subType,
      objectId: child.objectId,
      mandatory: child.mandatory,
      tags: child.tags,
      status: child.status,
      score: child.score,
      progress: child.progress,
      roots: child?.roots,
      maxScore: child.maxScore,
      minScore: child.minScore,
      notPassed: child.notPassed,
      notCompleted: child.notCompleted,
      isCorrected: child.isCorrected,
    });

    const childNodes = buildZigZagTree(
      arr,
      child.id,
      x,
      y,
      direction * -1,
      visited,
      edges,
      randomness,
      angleDeg
    );

    nodes.push(...childNodes);
  });

  return nodes;
}

export default function ConnectSectionPreview({
  lpgroupId,
  items,
  activeItem,
  allCategories,
  setSelectNode,
  selectNode,
  auth,
  layoutRandomness = {},
  angleDeg = 100,
}) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragStart = useRef(null);
  const containerRef = useRef(null);
  const userHasPannedRef = useRef(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const { nodes, edges, dimensions } = useMemo(() => {
    const randomness = { ...DEFAULT_RANDOMNESS, ...layoutRandomness };

    const visited = new Map();
    const edges = [];
    const root = items[0];

    const nodes = [
      {
        id: root.id,
        name: root.name || '',
        position: { x: 0, y: 0 },
        type: root.type,
        subType: root.subType,
        objectId: root.objectId,
        mandatory: root.mandatory,
        tags: root.tags,
        status: root.status,
        score: root.score,
        progress: root.progress,
        roots: root.roots,
        maxScore: root.maxScore,
        minScore: root.minScore,
        notPassed: root.notPassed,
        notCompleted: root.notCompleted,
        isCorrected: root.isCorrected,
      },
      ...buildZigZagTree(
        items,
        root.id,
        0,
        0,
        1,
        visited,
        edges,
        randomness,
        angleDeg
      ),
    ];

    visited.set(root.id, { x: 0, y: 0 });

    const minX = Math.min(...nodes.map((n) => n.position.x));
    const maxX = Math.max(...nodes.map((n) => n.position.x));
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const maxY = Math.max(...nodes.map((n) => n.position.y));

    const paddingX = 350;
    const paddingY = 1000;
    const width = maxX - minX + paddingX;
    const height = maxY - minY + paddingY;

    nodes.forEach((n) => {
      n.position.x = n.position.x - minX + paddingX / 2;
      n.position.y = n.position.y - minY + paddingY / 2;
    });

    return { nodes, edges, dimensions: { width, height } };
  }, [items, layoutRandomness, angleDeg]);

  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerSize({ width, height });
    }
  }, []);

  function clampOffset(newX, newY, dims, cont) {
    const minX = cont.width - dims.width;
    const maxX = 0;
    const minY = cont.height - dims.height;
    const maxY = 0;

    return {
      x: dims.width > cont.width ? Math.max(minX, Math.min(maxX, newX)) : 0,
      y: dims.height > cont.height ? Math.max(minY, Math.min(maxY, newY)) : 0,
    };
  }

  useEffect(() => {
    if (
      !containerRef.current ||
      nodes.length === 0 ||
      containerSize.height === 0
    )
      return;
    const activeNode =
      (activeItem && nodes.find((n) => n.id === activeItem.id)) || nodes[0];
    if (!activeNode) return;

    const NODE_HALF_W = 75;
    const NODE_HALF_H = 40;
    const targetX = activeNode.position.x + NODE_HALF_W;
    const targetY = activeNode.position.y + NODE_HALF_H;

    const containerCenterX = containerSize.width / 2;
    const containerCenterY = containerSize.height / 2;

    let newX = containerCenterX - targetX;
    let newY = containerCenterY - targetY;

    const clamped = clampOffset(newX, newY, dimensions, containerSize);
    setOffset(clamped);
  }, [containerSize]);

  const onMouseDown = (e) => {
    dragStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!dragStart.current) return;
    userHasPannedRef.current = true;
    let newX = e.clientX - dragStart.current.x;
    let newY = e.clientY - dragStart.current.y;

    const minX = containerSize.width - dimensions.width;
    const maxX = 0;
    const minY = containerSize.height - dimensions.height;
    const maxY = 0;

    const rootNode = nodes[0];
    const nodeHeight = 80;
    const containerCenterY = containerSize.height / 2;
    const initialOffsetY =
      containerCenterY - (rootNode.position.y + nodeHeight / 2);

    newX =
      dimensions.width > containerSize.width
        ? Math.max(minX, Math.min(maxX, newX))
        : 0;
    newY =
      dimensions.height > containerSize.height
        ? Math.max(minY, Math.min(maxY, newY))
        : initialOffsetY;

    setOffset({ x: newX, y: newY });
  };

  const onMouseUp = () => {
    dragStart.current = null;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  const onTouchStart = (e) => {
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - offset.x,
      y: touch.clientY - offset.y,
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  };

  const onTouchMove = (e) => {
    if (!dragStart.current) return;
    userHasPannedRef.current = true;
    e.preventDefault();
    const touch = e.touches[0];
    applyNewOffset(touch.clientX, touch.clientY);
  };

  const onTouchEnd = () => {
    dragStart.current = null;
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  };

  const applyNewOffset = (clientX, clientY) => {
    let newX = clientX - dragStart.current.x;
    let newY = clientY - dragStart.current.y;

    const minX = containerSize.width - dimensions.width;
    const maxX = 0;
    const minY = containerSize.height - dimensions.height;
    const maxY = 0;

    const rootNode = nodes[0];
    const nodeHeight = 80;
    const containerCenterY = containerSize.height / 2;
    const initialOffsetY =
      containerCenterY - (rootNode.position.y + nodeHeight / 2);

    newX =
      dimensions.width > containerSize.width
        ? Math.max(minX, Math.min(maxX, newX))
        : 0;
    newY =
      dimensions.height > containerSize.height
        ? Math.max(minY, Math.min(maxY, newY))
        : initialOffsetY;

    setOffset({ x: newX, y: newY });
  };

  useEffect(() => {
    if (!selectNode) {
      setSelectNode(activeItem);
    }
  }, []);

  return (
    <div ref={containerRef} className="relative flex h-[640px] overflow-hidden">
      {selectNode && (
        <ActiveCardPreview
          lpgroupId={lpgroupId}
          selectNode={selectNode}
          auth={auth}
          allCategories={allCategories}
        />
      )}

      <div className="t-0 l-0 absolute h-full w-full">
        <div
          className="relative my-auto touch-none"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            cursor: 'grab',
            minWidth: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            minHeight: '100%',
          }}
        >
          <svg
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: '-15px',
              left: '-50px',
            }}
          >
            {edges.map((edge, idx) => {
              const from = nodes.find((n) => n.id === edge.from);
              const to = nodes.find((n) => n.id === edge.to);

              if (!from || !to) return null;
              return (
                <line
                  key={`line-${idx}`}
                  x1={from.position.x + 75}
                  y1={from.position.y + 40}
                  x2={to.position.x + 75}
                  y2={to.position.y + 40}
                  stroke={'#3b82f6'}
                  strokeWidth={2}
                  strokeDasharray={to.mandatory ? '0' : '6,4'}
                />
              );
            })}
          </svg>

          {nodes?.map((node, index) => {
            let Icon;
            if (node?.tags && node?.type === 'learningpath') {
              const learningpathTags = allCategories?.find(
                (el) => el?.label === node?.tags.split(':')[1]
              );
              Icon = categoriesIcon(learningpathTags?.icon);
            } else {
              Icon = Theme;
            }
            return (
              <div
                key={node?.id}
                className={`absolute flex ${
                  selectNode?.id === node?.id &&
                  'border-[2px]  border-secondary-04'
                }`}
                style={{
                  cursor: 'pointer',
                  transform: 'rotate(-45deg) skew(15deg, 15deg)',
                  boxShadow: '0 1px 4px 0 rgb(0 0 0 / 20%)',
                  top: `${node?.position?.y}px`,
                  left: `${node?.position?.x}px`,
                  borderRadius: '4px',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectNode({
                    ...node,
                  });
                }}
              >
                <IconTile
                  Icon={
                    node.subType === 'training'
                      ? Training
                      : node.subType === 'learningpath'
                      ? Icon
                      : node.subType === 'milestone'
                      ? Flag
                      : node.subType === 'evaluation'
                      ? Evaluation
                      : node.subType === 'course'
                      ? DuoToneCourseFilled
                      : node.subType === 'assessment'
                      ? DuoToneAssessmentFilled
                      : node.subType === 'leervacature'
                      ? DuoToneVacancyFilled
                      : DuoToneCourseFilled
                  }
                  imageUrl={node.picture}
                  color={
                    node.subType === 'training'
                      ? 'green'
                      : node.subType === 'learningpath'
                      ? 'dark_blue'
                      : node.subType === 'milestone'
                      ? 'blue'
                      : node.subType === 'evaluation'
                      ? 'purple'
                      : node.subType === 'course'
                      ? 'yellow'
                      : node.subType === 'assessment'
                      ? 'red'
                      : node.subType === 'leervacature'
                      ? 'yellow'
                      : 'yellow'
                  }
                  size="small_md"
                  className={
                    node.subType === 'course' || node.subType === 'import'
                      ? 'fill-primary-06 text-primary-01'
                      : node.subType === 'assessment'
                      ? 'fill-tint-red-dark text-tint-red-light'
                      : node.subType === 'leervacature'
                      ? 'fill-error-04 text-error-04'
                      : ''
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
